import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import { validateConfig } from './config';

let s3Client: S3Client | null = null;

async function getS3Client() {
  try {
    // Always validate config before creating client
    const config = await validateConfig();
    
    // Create new client only if it doesn't exist or if we want to ensure fresh credentials
    if (!s3Client) {
      console.log('Initializing new S3 client...');
      s3Client = new S3Client({
        region: config.AWS_REGION,
        credentials: {
          accessKeyId: config.AWS_ACCESS_KEY_ID,
          secretAccessKey: config.AWS_SECRET_ACCESS_KEY,
        },
      });
      console.log('S3 client initialized successfully');
    }
    
    return s3Client;
  } catch (error) {
    console.error('Failed to initialize S3 client:', error);
    throw error;
  }
}

export async function uploadToS3(file: File): Promise<string> {
  try {
    const config = await validateConfig();
    
    // Validate file
    if (!file) {
      throw new Error('No file provided');
    }

    // Validate file size (100MB limit for better user experience)
    const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
    if (file.size > MAX_FILE_SIZE) {
      throw new Error('File size exceeds maximum limit of 100MB');
    }

    // Validate file type
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/webm'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Only MP4, MOV, and WebM files are supported.');
    }

    // Get S3 client
    const client = await getS3Client();
    
    // Generate unique key for the file
    const key = `uploads/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
    
    // Convert file to Uint8Array
    const arrayBuffer = await file.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    
    console.log('Uploading file to S3...', {
      bucket: config.AWS_S3_BUCKET,
      key,
      fileType: file.type,
      fileSize: file.size
    });

    try {
      const command = new PutObjectCommand({
        Bucket: config.AWS_S3_BUCKET,
        Key: key,
        Body: uint8Array,
        ContentType: file.type,
      });

      await client.send(command);
      console.log('File uploaded successfully');
      return `s3://${config.AWS_S3_BUCKET}/${key}`;
    } catch (error: any) {
      console.error('S3 upload error:', error);
      
      // Handle specific AWS errors
      if (error.name === 'AccessDenied') {
        throw new Error('Access denied. Please check your AWS credentials and bucket permissions.');
      }
      if (error.name === 'NoSuchBucket') {
        throw new Error(`S3 bucket "${config.AWS_S3_BUCKET}" not found. Please check your bucket name and region.`);
      }
      if (error.name === 'NetworkingError') {
        throw new Error('Network error. Please check your internet connection.');
      }
      
      throw new Error(`S3 upload failed: ${error.name} - ${error.message}`);
    }
  } catch (error) {
    console.error('Upload error:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to upload file to S3');
  }
}